import React, { useState, useEffect, useRef } from "react";
import { useUsersStore } from "../../stores/users.store";
import { useResources } from "../../hooks/useResources";
import { whitelistService } from "../../services/whitelist.service";
import type { Discipline } from "../../interfaces/apiInterfaces/disciplines";
import type { Role } from "../../interfaces/apiInterfaces/roles";
import type { AddUpdateUserModalProps } from "../../interfaces/componentInterfaces/userModal";
import CustomFormModal from "../modal/CustomFormModal";
import "./AddUpdateUserModal.scss";

const AddUpdateUserModal: React.FC<AddUpdateUserModalProps> = ({ isOpen, onClose, onSuccess, user }) => {
    const { getResourceValue } = useResources();
    const { addUser, updateUser, isLoading: isStoreLoading } = useUsersStore();

    const [formData, setFormData] = useState({
        user_first_name: "",
        user_last_name: "",
        user_email: "",
    });

    const [selectedDisciplines, setSelectedDisciplines] = useState<string[]>([]);
    const [selectedRole, setSelectedRole] = useState<string>("");

    const [disciplines, setDisciplines] = useState<Discipline[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDisciplineDropdownOpen, setIsDisciplineDropdownOpen] = useState(false);
    const disciplineDropdownRef = useRef<HTMLDivElement>(null);

    const isEditMode = !!user;

    useEffect(() => {
        if (isOpen) {
            loadInitialData();
            if (user) {
                setFormData({
                    user_first_name: user.user_first_name,
                    user_last_name: user.user_last_name,
                    user_email: user.user_email,
                });
                setSelectedDisciplines(user.disciplines.map(d => d.discipline_id));
                setSelectedRole(user.roles[0]?.role_id || "");
            } else {
                setFormData({
                    user_first_name: "",
                    user_last_name: "",
                    user_email: "",
                });
                setSelectedDisciplines([]);
                setSelectedRole("");
            }
            setError(null);
        }
    }, [isOpen, user]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (disciplineDropdownRef.current && !disciplineDropdownRef.current.contains(event.target as Node)) {
                setIsDisciplineDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const [disciplinesRes, rolesRes] = await Promise.all([
                whitelistService.getDisciplines(),
                whitelistService.getRoles(),
            ]);
            setDisciplines(disciplinesRes.data);
            setRoles(rolesRes.data);
        } catch (err) {
            console.error("Failed to load initial data:", err);
            setError(getResourceValue("failed_to_load_data", "Failed to load disciplines or roles"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleDisciplineToggle = (id: string) => {
        setSelectedDisciplines((prev) =>
            prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!formData.user_first_name.trim() || !formData.user_last_name.trim() || !formData.user_email.trim()) {
            setError(getResourceValue("all_fields_required", "First name, last name, and email are required"));
            return;
        }

        if (selectedDisciplines.length === 0) {
            setError(getResourceValue("select_disciplines_error", "Please select at least one discipline"));
            return;
        }

        if (!formData.user_email.toLowerCase().endsWith("@nagwa.com")) {
            setError(getResourceValue("email_domain_error", "Email must be a @nagwa.com address"));
            return;
        }

        try {
            if (isEditMode && user) {
                await updateUser(user.user_id, {
                    user_first_name: formData.user_first_name.trim(),
                    user_last_name: formData.user_last_name.trim(),
                    user_email: formData.user_email.trim(),
                    discipline_ids: selectedDisciplines,
                    role_ids: selectedRole ? [selectedRole] : [],
                });
            } else {
                await addUser({
                    user_first_name: formData.user_first_name.trim(),
                    user_last_name: formData.user_last_name.trim(),
                    user_email: formData.user_email.trim(),
                    discipline_ids: selectedDisciplines,
                    role_ids: selectedRole ? [selectedRole] : [],
                });
            }
            if (onSuccess) onSuccess();
            onClose();
        } catch (err: any) {
            const fallbackError = isEditMode ? "Failed to update user" : "Failed to add user";
            const resourceKey = isEditMode ? "update_user_failed" : "add_user_failed";
            setError(err?.message || getResourceValue(resourceKey, fallbackError));
        }
    };

    const getSelectedDisciplinesText = () => {
        if (selectedDisciplines.length === 0) return getResourceValue("select_disciplines", "Select Disciplines");
        return selectedDisciplines
            .map((id) => disciplines.find((d) => d.discipline_id === id)?.discipline_title)
            .filter(Boolean)
            .join(", ");
    };

    const isFormValid = () => {
        return (
            formData.user_first_name.trim() !== "" &&
            formData.user_last_name.trim() !== "" &&
            formData.user_email.trim() !== "" &&
            selectedDisciplines.length > 0 &&
            selectedRole !== ""
        );
    };

    return (
        <CustomFormModal
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={handleSubmit}
            title={isEditMode ? getResourceValue("edit_user", "Edit User") : getResourceValue("add_user", "Add User")}
            submitButtonText={isEditMode ? getResourceValue("save", "Save") : getResourceValue("add_user", "Add User")}
            isLoading={isStoreLoading || isLoading}
            error={error}
            isSubmitDisabled={!isFormValid()}
        >
            <div className="add-user-form__row">
                <div className="add-user-form__field">
                    <input
                        type="text"
                        name="user_first_name"
                        placeholder={getResourceValue("first_name", "First Name")}
                        value={formData.user_first_name}
                        onChange={handleInputChange}
                        autoComplete="off"
                        className="add-user-form__input"
                    />
                </div>
                <div className="add-user-form__field">
                    <input
                        type="text"
                        name="user_last_name"
                        placeholder={getResourceValue("last_name", "Last Name")}
                        value={formData.user_last_name}
                        onChange={handleInputChange}
                        autoComplete="off"
                        className="add-user-form__input"
                    />
                </div>
            </div>

            <div className="add-user-form__field">
                <input
                    type="email"
                    name="user_email"
                    placeholder={getResourceValue("email_placeholder", "Email")}
                    value={formData.user_email}
                    onChange={handleInputChange}
                    autoComplete="off"
                    className="add-user-form__input"
                />
            </div>

            <div className="add-user-form__field" ref={disciplineDropdownRef}>
                <div
                    className={`add-user-form__custom-select ${isDisciplineDropdownOpen ? 'open' : ''}`}
                    onClick={() => setIsDisciplineDropdownOpen(!isDisciplineDropdownOpen)}
                >
                    <div className={`add-user-form__custom-select-value ${selectedDisciplines.length === 0 ? 'placeholder' : ''}`}>
                        {getSelectedDisciplinesText()}
                    </div>
                    <i className="f-icon chevron-down-icon"></i>

                    {isDisciplineDropdownOpen && (
                        <div className="add-user-form__custom-select-options" onClick={(e) => e.stopPropagation()}>
                            {isLoading ? (
                                <div className="add-user-form__option loading">Loading...</div>
                            ) : (
                                disciplines.map((discipline) => (
                                    <div
                                        key={discipline.discipline_id}
                                        className={`add-user-form__option ${selectedDisciplines.includes(discipline.discipline_id) ? 'selected' : ''}`}
                                        onClick={() => handleDisciplineToggle(discipline.discipline_id)}
                                    >
                                        <span>{discipline.discipline_title}</span>
                                        {selectedDisciplines.includes(discipline.discipline_id) && (
                                            <i className="f-icon check-icon"></i>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="add-user-form__field">
                <div className="add-user-form__select-wrapper">
                    <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className={`add-user-form__select ${selectedRole === "" ? 'placeholder' : ''}`}
                    >
                        <option value="" disabled>{getResourceValue("select_role", "Select Role")}</option>
                        {roles.map((role) => (
                            <option key={role.role_id} value={role.role_id}>
                                {role.role_name}
                            </option>
                        ))}
                    </select>
                    <i className="f-icon chevron-down-icon"></i>
                </div>
            </div>
        </CustomFormModal>
    );
};

export default AddUpdateUserModal;
